import { Injectable, inject } from "@angular/core";
import { Actions, createEffect, ofType } from "@ngrx/effects";
import { concatLatestFrom } from "@ngrx/operators";
import { Store } from "@ngrx/store";
import { iif, of } from "rxjs";
import { catchError, map, mergeMap } from "rxjs/operators";
import { USER_CONFIG } from "../../user-config";
import { UserService } from "../user.service";
import {
	changeCurrentSite,
	changeCurrentSiteSuccess,
	loginUser,
	loginUserFailed,
	loginUserRepeat,
	loginUserSuccess,
} from "./user.actions";
import { loggedIn } from "./user.selectors";

@Injectable()
export class UserEffects {
	private readonly actions$ = inject(Actions);
	private readonly store = inject(Store);
	private readonly userInfoService = inject(UserService);
	private readonly config = inject(USER_CONFIG);

	/*
	The Load User Information Effect:
	Checks if already logged in via selector.
  */
	loadUserInformation$ = createEffect(() =>
		this.actions$.pipe(
			ofType(loginUser),
			concatLatestFrom(() => this.store.select(loggedIn)),
			mergeMap(([action, isLoggedIn]) =>
				iif(
					() => isLoggedIn,
					of(loginUserRepeat()),
					this.userInfoService.getUserInfo().pipe(
						map((userInfo) => loginUserSuccess({ userInfo })),
						catchError(() => of(loginUserFailed())),
					),
				),
			),
		),
	);

	/*
	The set Current Site Effect:
	Persists the selection (localStorage in demo mode, PATCH /users/me/active-site otherwise).
  */
	setCurrentSiteSideEffect$ = createEffect(() =>
		this.actions$.pipe(
			ofType(changeCurrentSite),
			mergeMap((action) => {
				if (this.config.demo) {
					localStorage.setItem("siteIdOption", action.newSiteId);
					return of(changeCurrentSiteSuccess({ newSiteId: action.newSiteId }));
				}

				return this.userInfoService.setActiveSite(action.newSiteId).pipe(
					map(() => changeCurrentSiteSuccess({ newSiteId: action.newSiteId })),
					catchError(() => of(changeCurrentSiteSuccess({ newSiteId: action.newSiteId }))),
				);
			}),
		),
	);
}
